import { useEffect } from "react";

import { Button } from "@fluentui/react-components";
import { SendFilled, Stop24Filled } from "@fluentui/react-icons";
import { useAtom, useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { chatHistoryAtom, chatIdAtom } from "store";
import { selectedPackageUserAtom } from "store/UIBenefitsPage";
import styled from "styled-components";

import { TextareaField } from "components/controls/TextareaField";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import useEmployee from "hooks/useEmployee";
import { useSendMsgToChatbot } from "state/useSendMsgToChatbot";

type Props = {
  historyContainerRef: React.RefObject<HTMLDivElement>;
  newMessageState: {
    message: string;
    sent: boolean;
  };
  setNewMessageState: ({ message, sent }: { message: string; sent: boolean }) => void;
};

export function NewMessage({ historyContainerRef, newMessageState, setNewMessageState }: Props) {
  const theme = useAppTheme();
  // const [message, setMessage] = useState("");
  const { t } = useTranslation();
  const { employeeData } = useEmployee();

  const packageUser = useAtomValue(selectedPackageUserAtom);

  const [chatHistory, setChatHistory] = useAtom(chatHistoryAtom);

  // const { sendNotification } = useSendPushNotification();
  const { mutate: onSendMsgToChatbot, isLoading, cancel: cancelSendMsg } = useSendMsgToChatbot();

  const handleStop = () => {
    cancelSendMsg();
    setNewMessageState({
      message: newMessageState?.message,
      sent: false,
    });
  };

  const chatSessionId = useAtomValue(chatIdAtom);

  const scrollToBottom = () => {
    if (historyContainerRef?.current) {
      (historyContainerRef?.current as any).scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const handleSendMessage = () => {
    setNewMessageState({
      message: newMessageState?.message,
      sent: true,
    });
    setTimeout(() => {
      scrollToBottom();
    }, 50);
    onSendMsgToChatbot(
      {
        payload: {
          Id: chatSessionId || "",
          User: `${employeeData?.firstName} ${employeeData?.lastName}`,
          UserId: employeeData?.employeeLoginEmailAddress || "",
          ForUserId: packageUser?.employeeDependentTag
            ? packageUser?.employeeDependentTag?.toString()
            : packageUser?.employeeLoginEmailAddress,
          Question: newMessageState?.message || "",
          MessageHistory: chatHistory as any,
        },
      },
      {
        onSuccess: (response) => {
          setChatHistory((prev) => {
            return [
              ...prev,
              {
                PrevQuestion: newMessageState?.message,
                PrevAnswer: response,
                ForUserId: packageUser?.employeeDependentTag
                  ? packageUser?.employeeDependentTag?.toString()
                  : packageUser?.employeeLoginEmailAddress,
              },
            ];
          });
          setNewMessageState({
            message: "",
            sent: false,
          });
        },
      }
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents a new line in the textarea

      handleSendMessage();
      // You can trigger any action here, such as submitting the text
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const sendDisabled = isLoading || !newMessageState.message || !chatSessionId;

  return (
    <Flex width={"100%"} $justify="space-between" align="flex-end" wrap="nowrap">
      <TextareaField
        disabled={isLoading || !chatSessionId}
        noLabel
        label=""
        noErrorSection
        name="message"
        value={newMessageState.message}
        onKeyDown={handleKeyDown}
        setFieldValue={(name, value) => {
          setNewMessageState({
            message: value,
            sent: false,
          });
        }}
        style={{
          border: `1px solid ${theme.palette.neutralLight}`,
          fontSize: "16px",
        }}
        placeholder={t("hbh.writeMessage.label")}
        error={""}
        rows={4}
        maxLength={400}
        resize="none"
      />
      <StyledButton
        appearance="primary"
        size="large"
        disabled={isLoading ? false : sendDisabled}
        onClick={() => {
          if (isLoading) {
            handleStop();
          } else {
            handleSendMessage();
          }
        }}
        icon={
          isLoading ? (
            <Stop24Filled
              style={{
                color: theme.palette.white,
              }}
            />
          ) : (
            <SendFilled
              style={{
                color: theme.palette.white,
                transform: "rotate(-45deg)",
                position: "absolute",
                left: "9px",
                top: "6px",
              }}
            />
          )
        }
        shape="circular"
      />
    </Flex>
  );
}

export default NewMessage;

const StyledButton = styled(Button)`
  position: relative;
  &:hover {
    opacity: 0.5;
  }
`;
